import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Stack,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { RangeDatepicker } from 'chakra-dayzed-datepicker';
import { djs } from '@duke-hq/djs';
import { ArrowRightFromLine, ChevronDown } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useCSVDownloader, usePapaParse } from 'react-papaparse';
import { withPermission } from '../withPermission';
import { ProviderSelector } from './ProviderSelector';

const defaultEndDate = new Date();
const defaultStartDate = new Date(defaultEndDate);
defaultStartDate.setDate(defaultStartDate.getDate() - 1); // 1 day ago

export const InvoicesPage = withPermission('canViewFinances', () => {
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const toast = useToast();
  const [selectedDates, setSelectedDates] = useState<Date[]>([defaultStartDate, defaultEndDate]);
  const [startDate, endDate]: [Date, Date | undefined] = selectedDates as [Date, Date | undefined];
  const { CSVDownloader } = useCSVDownloader();
  const { jsonToCSV } = usePapaParse();
  const [auditProviders, setAuditProviders] = useState<string[]>([]);
  const handleAuditFilterChange = useCallback((newFilters: { tenantIds?: string[] }) => {
    if (Array.isArray(newFilters?.tenantIds) && newFilters.tenantIds.length > 0) {
      setAuditProviders(newFilters.tenantIds);
    } else {
      setAuditProviders([]);
    }
  }, []);

  const { data: tenants } = trpcAdmin.providers.all.useQuery({}, { initialData: [] });
  const providerOptions: {
    id: string;
    value: string;
    label: string;
  }[] = useMemo(() => {
    return tenants
      .map((tenant) => ({
        id: `${tenant.id}`,
        value: `${tenant.id}`,
        label: `${tenant.is_billable ? 'Billable -' : ''} ${tenant.subdomain}`,
      }))
      .sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
  }, [tenants]);

  const downloadCSV = trpcAdmin.finances.invoice.download.useMutation({
    onSuccess: (res) => {
      if (!res.csv) {
        toast({
          title: t`No data found`,
          description: t`No invoice data found for the selected date range.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
          variant: 'left-accent',
        });
        return;
      }

      // Create a Blob from the CSV string
      const blob = new Blob([res.csv], { type: 'text/csv;charset=utf-8;' });

      // Create a download link and trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}.csv`);
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () => {
      toast({
        title: t`Error downloading CSV`,
        description: t`An error occurred while downloading the CSV file. Please try again.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const downloadFinancialEvents = trpcAdmin.finances.financialEvents.create.useMutation({
    onSuccess: (res) => {
      if (res.length === 0) {
        toast({
          title: t`No data found`,
          description: t`No financial events found for the selected date range.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
          variant: 'left-accent',
        });
      }
    },
    onError: (error) => {
      toast({
        title: t`Error downloading CSV`,
        description:
          error.message ||
          t`An error occurred while downloading the financial events CSV file. Please try again.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const downloadSalesReport = trpcAdmin.finances.salesReport.create.useMutation({
    onSuccess: (res) => {
      if (res.length === 0) {
        toast({
          title: t`No data found`,
          description: t`No sales data was found for the selected date range.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
          variant: 'left-accent',
        });
      }
    },
    onError: (error) => {
      toast({
        title: t`Error downloading CSV`,
        description:
          error.message ||
          t`An error occurred while downloading the sales report CSV file. Please try again.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const billingSitesReport = trpcAdmin.finances.billingSitesReport.create.useMutation({
    onSuccess: (res) => {
      if (res.length === 0) {
        toast({
          title: t`No data found`,
          description: t`No billing sites data was found for the selected date range.`,
          status: 'info',
          duration: 5000,
          isClosable: true,
          variant: 'left-accent',
        });
      }
    },
    onError: (error) => {
      toast({
        title: t`Error downloading CSV`,
        description:
          error.message ||
          t`An error occurred while downloading the billing sites report CSV file. Please try again.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const { refetch: refetchStudentAuditReport, isFetching: isStudentAuditReportFetching } =
    trpcAdmin.finances.studentAuditReport.getAllBillableProviders.useQuery(
      { tenantIds: auditProviders },
      {
        enabled: false,
        retry: false,
      },
    );
  const { refetch: refetchECEnrolmentsReport, isFetching: isECEnrolmentsReportFetching } =
    trpcAdmin.finances.ecEnrolmentsReport.fetchECEnrolmentReport.useQuery(undefined, {
      enabled: false,
      retry: false,
    });

  const { refetch: refetchNotYetBillableReport, isFetching: isNotYetBillableReportFetching } =
    trpcAdmin.finances.notYetBillableReport.fetchNotYetBillableReport.useQuery(undefined, {
      enabled: false,
      retry: false,
    });

  const { refetch: refetchGraduationsReport, isFetching: isGraduationsReportFetching } =
    trpcAdmin.finances.graduationsReport.fetchGraduationsReport.useQuery(
      {
        fromDate: startDate ?? defaultStartDate,
        toDate: endDate ?? defaultEndDate,
      },
      {
        enabled: false,
        retry: false,
      },
    );

  const {
    refetch: refetchSecondSemesterReferenceReport,
    isFetching: isSecondSemesterReferenceReportFetching,
  } = trpcAdmin.finances.secondSemesterReferenceReport.fetchSecondSemesterReferenceReport.useQuery(
    undefined,
    {
      enabled: false,
      retry: false,
    },
  );

  const handleDownloadCSVClick = useCallback(() => {
    void (async () =>
      downloadCSV.mutateAsync({
        fromDate: startDate ?? defaultStartDate,
        toDate: endDate ?? defaultEndDate,
      }))();
  }, [downloadCSV, startDate, endDate]);

  const setDateRangeFromNow = useCallback((days: number) => {
    const end = djs().endOf('day').toDate();
    const start = djs().subtract(days, 'days').startOf('day').toDate();
    setSelectedDates([start, end]);
  }, []);

  const setDateRangeFromStartAndEndOfDays = useCallback((range: Date[]) => {
    const newDateRange: Date[] = [];
    if (range[0]) {
      newDateRange.push(djs(range[0]).startOf('day').toDate());
    }
    if (range[1]) {
      newDateRange.push(djs(range[1]).endOf('day').toDate());
    }
    setSelectedDates(newDateRange);
  }, []);

  const showGenericErrorToast = useCallback(() => {
    toast({
      title: t`Error generating report`,
      description: t`An error occurred while generating the report. Please try again.`,
      status: 'error',
      duration: 5000,
      isClosable: true,
    });
  }, [toast]);

  const handleStudentAuditReportFetch = useCallback(async () => {
    const { data, error } = await refetchStudentAuditReport();

    if (error) {
      showGenericErrorToast();

      if (error instanceof Error) {
        throw error;
      }
    }

    if (!data) {
      return [];
    }

    return jsonToCSV(data);
  }, [jsonToCSV, refetchStudentAuditReport, showGenericErrorToast]);

  const handleECEnrolmentsReportFetch = useCallback(async () => {
    const { data, error } = await refetchECEnrolmentsReport();

    if (error) {
      showGenericErrorToast();

      if (error instanceof Error) {
        throw error;
      }
    }

    if (!data) {
      return [];
    }

    return jsonToCSV(data);
  }, [jsonToCSV, refetchECEnrolmentsReport, showGenericErrorToast]);

  const handleGraduationsReportFetch = useCallback(async () => {
    const { data, error } = await refetchGraduationsReport();

    if (error) {
      showGenericErrorToast();

      if (error instanceof Error) {
        throw error;
      }
    }

    if (!data) {
      return [];
    }

    return jsonToCSV(data);
  }, [jsonToCSV, refetchGraduationsReport, showGenericErrorToast]);

  const handleSecondSemesterReferenceReportFetch = useCallback(async () => {
    const { data, error } = await refetchSecondSemesterReferenceReport();

    if (error) {
      showGenericErrorToast();

      if (error instanceof Error) {
        throw error;
      }
    }

    if (!data) {
      return [];
    }

    return jsonToCSV(data);
  }, [jsonToCSV, refetchSecondSemesterReferenceReport, showGenericErrorToast]);

  const handleNotYetBillableReportFetch = useCallback(async () => {
    const { data, error } = await refetchNotYetBillableReport();

    if (error) {
      showGenericErrorToast();
      if (error instanceof Error) {
        throw error;
      }
    }

    if (!data) {
      return [];
    }

    return jsonToCSV(data);
  }, [jsonToCSV, refetchNotYetBillableReport, showGenericErrorToast]);

  return (
    <TableViewLayout
      pageHeader={[
        !isExpanded && (
          <IconButton
            aria-label="expand secondary sidebar"
            icon={<ArrowRightFromLine />}
            variant="tertiary"
            onClick={toggleExpandSubSidebar}
            size="md"
          />
        ),
        <PageHeading key="page-heading">
          <Trans>Invoices</Trans>
        </PageHeading>,
      ]}
      tableActionsRight={
        <VStack spacing={8} p={4} align="end">
          <HStack justifyContent="flex-end" alignItems="flex-end">
            <FormControl id="date-range" isRequired>
              <FormLabel htmlFor="date-range" mt="0">
                <Trans>Date Range</Trans>
              </FormLabel>
              <ButtonGroup isAttached variant="outline" isDisabled={downloadCSV.isPending}>
                <RangeDatepicker
                  triggerVariant="input"
                  selectedDates={selectedDates}
                  onDateChange={setDateRangeFromStartAndEndOfDays}
                  configs={{ dateFormat: 'dd-MM-yyyy' }}
                />
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label={t`Date range select options`}
                    icon={<Icon as={ChevronDown} />}
                    variant="unstyled"
                  />
                  <MenuList>
                    <MenuItem onClick={() => setDateRangeFromNow(1)}>
                      <Trans>Last 24 hours</Trans>
                    </MenuItem>
                    <MenuItem onClick={() => setDateRangeFromNow(3)}>
                      <Trans>Last 3 days</Trans>
                    </MenuItem>
                    <MenuItem onClick={() => setDateRangeFromNow(7)}>
                      <Trans>Last 7 days</Trans>
                    </MenuItem>
                    <MenuItem onClick={() => setDateRangeFromNow(30)}>
                      <Trans>Last 30 days</Trans>
                    </MenuItem>
                  </MenuList>
                </Menu>
              </ButtonGroup>
            </FormControl>
            <Button
              variant="primary"
              onClick={handleDownloadCSVClick}
              isLoading={downloadCSV.isPending}
            >
              <Trans>Download CSV</Trans>
            </Button>
          </HStack>

          <VStack justifyContent="flex-end" alignItems="flex-end">
            <h4>Date range selected:</h4>
            <h4>
              <b>Start: </b>
              {startDate ? djs(startDate).format('dddd MMMM D, YYYY h:mm A') : 'Not selected'}
            </h4>
            <h4>
              <b>End: </b>
              {endDate ? djs(endDate).format('dddd MMMM D, YYYY h:mm A') : 'Not selected'}
            </h4>
          </VStack>

          <HStack
            justifyContent="flex-end"
            alignItems="flex-end"
            data-testid="audit-provider-selector"
          >
            <ProviderSelector
              selectedProviders={auditProviders}
              providerOptions={providerOptions}
              onChange={handleAuditFilterChange}
            />
            <CSVDownloader
              type="link"
              filename={`student_audit_report_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
              data={async () => await handleStudentAuditReportFetch()}
            >
              <Button variant="primary" isLoading={isStudentAuditReportFetching}>
                <Trans>Download Student Audit Report</Trans>
              </Button>
            </CSVDownloader>
          </HStack>

          <HStack
            justifyContent="flex-end"
            alignItems="flex-end"
            data-testid="nyb-provider-selector"
          >
            <CSVDownloader
              type="link"
              filename={`not_yet_billable_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
              data={async () => await handleNotYetBillableReportFetch()}
            >
              <Button variant="primary" isLoading={isNotYetBillableReportFetching}>
                <Trans>Download Not yet billable Report</Trans>
              </Button>
            </CSVDownloader>
          </HStack>

          <HStack justifyContent="flex-end" alignItems="flex-end">
            <CSVDownloader
              type="link"
              filename={`financial_events_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
              data={async () =>
                jsonToCSV(
                  await downloadFinancialEvents.mutateAsync({
                    fromDate: startDate ?? defaultStartDate,
                    toDate: endDate ?? defaultEndDate,
                  }),
                )
              }
            >
              <Button variant="primary" isLoading={downloadFinancialEvents.isPending}>
                <Trans>Download Financial Events</Trans>
              </Button>
            </CSVDownloader>
          </HStack>

          <HStack justifyContent="flex-end" alignItems="flex-end">
            <CSVDownloader
              type="link"
              filename={`sales_report_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
              data={async () =>
                jsonToCSV(
                  await downloadSalesReport.mutateAsync({
                    fromDate: startDate ?? defaultStartDate,
                    toDate: endDate ?? defaultEndDate,
                    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                  }),
                )
              }
            >
              <Button variant="primary" isLoading={downloadSalesReport.isPending}>
                <Trans>Download Sales Report</Trans>
              </Button>
            </CSVDownloader>
          </HStack>

          <HStack justifyContent="flex-end" alignItems="flex-end">
            <CSVDownloader
              type="link"
              filename={`billing_sites_report_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
              data={async () =>
                jsonToCSV(
                  await billingSitesReport.mutateAsync({
                    fromDate: startDate ?? defaultStartDate,
                    toDate: endDate ?? defaultEndDate,
                  }),
                )
              }
            >
              <Button variant="primary" isLoading={billingSitesReport.isPending}>
                <Trans>Billing Sites Report</Trans>
              </Button>
            </CSVDownloader>
          </HStack>

          <HStack justifyContent="flex-end" alignItems="flex-end">
            <CSVDownloader
              type="link"
              filename={`ec_enrolment_counts_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
              data={async () => await handleECEnrolmentsReportFetch()}
            >
              <Button variant="primary" isLoading={isECEnrolmentsReportFetching}>
                <Trans>Download EC Enrolments</Trans>
              </Button>
            </CSVDownloader>
          </HStack>

          <HStack justifyContent="flex-end" alignItems="flex-end">
            <CSVDownloader
              type="link"
              filename={`graduations_report_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
              data={async () => await handleGraduationsReportFetch()}
            >
              <Button variant="primary" isLoading={isGraduationsReportFetching}>
                <Trans>Download Graduations Report</Trans>
              </Button>
            </CSVDownloader>
          </HStack>

          <HStack justifyContent="flex-end" alignItems="flex-end">
            <CSVDownloader
              type="link"
              filename={`second_semester_reference_report_${djs(new Date()).format('YYYY-MM-DD_HHMMSSS')}`}
              data={async () => await handleSecondSemesterReferenceReportFetch()}
            >
              <Button variant="primary" isLoading={isSecondSemesterReferenceReportFetching}>
                <Trans>Download 2nd Semester Reference Report</Trans>
              </Button>
            </CSVDownloader>
          </HStack>
        </VStack>
      }
      table={
        <Box
          bg="bg.surface"
          boxShadow={{ base: 'none', md: 'sm' }}
          borderRadius={{ base: 'none', md: 'lg' }}
        >
          <Stack spacing="5">
            <Box overflowX="auto" p={10}></Box>
          </Stack>
        </Box>
      }
    />
  );
});
